import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../stores/authStore';
import { useOnboarding, ONBOARDING_STEP_ORDER } from '../../stores/onboardingStore';
import { confirmDialog } from '../ui/ConfirmDialog';
import { TourStep, type ArrowDirection } from './TourStep';

// Sprint-12 — first-run Dashboard tour.
//
// Mounted at AppShell. The component renders nothing while
// onboarding_completed_at IS NOT NULL, so it's effectively free for
// users who already finished the tour. When active, it sits in a
// portal so it can dim/scrim the entire viewport regardless of which
// AppShell child page is mounted under <Outlet />.

interface StepDef {
  key: typeof ONBOARDING_STEP_ORDER[number];
  title: string;
  body: string[];
  /** CSS selector for the DOM element to spotlight. */
  targetSelector?: string;
  arrow: ArrowDirection;
  /** Override the primary button label. */
  primaryLabel?: string;
}

// Step content. Targets:
//  1. The user/theme/avatar cluster on the top-right of the AppShell
//     header — that's the part of the screen closest to the Chrome
//     toolbar where the extension icon will appear once installed.
//  2. The Console (Dashboard) link in the primary nav — center of the
//     screen attention, anchors the "make your first snip" explanation.
//  3. The avatar cluster again (no header element corresponds 1:1 to
//     the Share Panel which lives inside CreatorStudio) — paired with
//     a centered card so the share message reads as a wrap-up.
const STEPS: StepDef[] = [
  {
    key: 'extension',
    title: 'Install the Worksnip extension',
    body: [
      'Worksnip captures work from your browser via a Chrome extension. Click the puzzle-piece icon in Chrome\'s toolbar (top-right) to find or pin it.',
      'If you haven\'t installed it yet, grab it from the Chrome Web Store, then come back here.',
    ],
    targetSelector: '[data-tour="header-user"]',
    arrow: 'up-right',
    primaryLabel: "I've installed it",
  },
  {
    key: 'capture',
    title: 'Make your first snip',
    body: [
      'Open the extension\'s side panel on any tab you want to document, then hit Start capture. Click through your workflow as you normally would — Worksnip records every click, type, and screen.',
      'When you\'re done, stop the capture and a new guide appears here in your Console.',
    ],
    targetSelector: '[data-tour="nav-console"]',
    arrow: 'up',
    primaryLabel: "Got it",
  },
  {
    key: 'share',
    title: 'Share what you captured',
    body: [
      'Inside any guide you can pick a viewing mode (Flick, Brief, Reel, or Beat), then use the Share Panel to send a link to teammates or generate a public viewer URL.',
      'That\'s the loop: capture → review → share. You\'re ready.',
    ],
    arrow: 'none',
    primaryLabel: "Let's go",
  },
];

export function OnboardingTour() {
  const user = useAuth(s => s.user);
  const hydrated         = useOnboarding(s => s.hydrated);
  const completedAt      = useOnboarding(s => s.completedAt);
  const currentStepIndex = useOnboarding(s => s.currentStepIndex);
  const hydrateFromUser  = useOnboarding(s => s.hydrateFromUser);
  const nextStep         = useOnboarding(s => s.next);
  const completeAll      = useOnboarding(s => s.completeAll);

  // Hydrate once when we get a user record. Re-runs are cheap and
  // idempotent (the store's hydrateFromUser checks its own flag before
  // resetting currentStepIndex).
  useEffect(() => {
    if (user) hydrateFromUser(user);
  }, [user, hydrateFromUser]);

  // Esc to dismiss the whole tour — with a confirm to avoid accidents.
  useEffect(() => {
    if (completedAt || currentStepIndex === null) return;
    function onKey(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      confirmDialog({
        title: 'Skip onboarding?',
        message: 'You can restart the tour anytime from your admin settings.',
        confirmLabel: 'Skip tour',
        cancelLabel: 'Keep going',
      }).then((ok) => { if (ok) completeAll(); });
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [completedAt, currentStepIndex, completeAll]);

  // Gates: don't render while hydrating, when the tour is closed, or
  // for users who already finished it.
  if (!user) return null;
  if (!hydrated) return null;
  if (completedAt) return null;
  if (currentStepIndex === null) return null;
  const step = STEPS[currentStepIndex];
  if (!step) return null;

  return createPortal(
    <TourStep
      stepNumber={currentStepIndex + 1}
      totalSteps={STEPS.length}
      title={step.title}
      body={step.body}
      targetSelector={step.targetSelector}
      arrow={step.arrow}
      primaryLabel={step.primaryLabel}
      onPrimary={() => { void nextStep(); }}
      secondaryLabel={currentStepIndex === STEPS.length - 1 ? 'Done' : 'Skip'}
      onSecondary={() => {
        // Skip on a non-last step is the same as Esc — confirm + skip-all.
        if (currentStepIndex === STEPS.length - 1) {
          void completeAll();
          return;
        }
        confirmDialog({
          title: 'Skip onboarding?',
          message: 'You can restart the tour anytime from your admin settings.',
          confirmLabel: 'Skip tour',
          cancelLabel: 'Keep going',
        }).then((ok) => { if (ok) void completeAll(); });
      }}
    />,
    document.body,
  );
}
